/**
 * F12 Section 5.1 — Admin venue-photo moderation queue.
 *
 * Route: /venue-photos
 * Permission: venue_photos:moderate
 *
 * Tabbed status filter (Pending / Approved / Rejected) → offset-paged
 * `<Table>` (limit 20). Each row shows a square thumbnail (`variants.cardUrl`
 * falling back to `cdnUrl` for legacy / pre-Sharp-pipeline rows), the venue
 * name + slug, the uploader id, and the upload timestamp. "Review" navigates
 * to `/venue-photos/:photoId`.
 *
 * F61 (A1) — no poll of its own any more: `GET /admin/venue-photos` is an
 * audited read, so the old 60-s re-list wrote an audit row a minute. The page
 * silently re-lists when the shared (unaudited) `venuePhotos` nav count
 * changes — i.e. a new upload or a decision elsewhere — and on every visit
 * (decisions happen on the detail page, so coming back reloads).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  Banner,
  Button,
  EmptyState,
  Pagination,
  Skeleton,
  Tab,
  TabList,
  TabPanel,
  Table,
  Tabs,
  type TableColumn,
} from '@bravemove/ui-web';
import { UIIcon } from '@bravemove/design-tokens/icons';
import type { VenuePhotoModerationStatus } from '@bravemove/types';
import { adminVenuePhotoApi, type VenuePhotoQueueItem } from '../../api/venue-photo.js';
import { translateApiError } from '../../auth/errors.js';
import { PageHeader } from '../../components/PageHeader';
import { useNavCounts } from '../../navCounts/useNavCount';

const PAGE_LIMIT = 20;
const DEFAULT_STATUS: VenuePhotoModerationStatus = 'pending';

const STATUS_TABS: ReadonlyArray<{
  value: VenuePhotoModerationStatus;
  labelKey: string;
}> = [
  { value: 'pending', labelKey: 'admin:venue-editor.queue.filterPending' },
  { value: 'approved', labelKey: 'admin:venue-editor.queue.filterApproved' },
  { value: 'rejected', labelKey: 'admin:venue-editor.queue.filterRejected' },
];

function isStatus(v: string | null): v is VenuePhotoModerationStatus {
  return v === 'pending' || v === 'approved' || v === 'rejected';
}

function formatRelative(iso: string | null | undefined, locale: string): string {
  if (!iso) return '—';
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return '—';
  const diffMs = target - Date.now();
  const diffMin = Math.round(diffMs / 60_000);
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    const absMin = Math.abs(diffMin);
    if (absMin < 60) return rtf.format(diffMin, 'minute');
    const diffHour = Math.round(diffMin / 60);
    if (Math.abs(diffHour) < 24) return rtf.format(diffHour, 'hour');
    const diffDay = Math.round(diffHour / 24);
    return rtf.format(diffDay, 'day');
  } catch {
    return iso;
  }
}

export function VenuePhotoQueuePage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const statusParam = (() => {
    const raw = searchParams.get('status');
    return isStatus(raw) ? raw : DEFAULT_STATUS;
  })();
  const pageParam = (() => {
    const raw = searchParams.get('page');
    const n = raw ? parseInt(raw, 10) : 1;
    return Number.isFinite(n) && n > 0 ? n : 1;
  })();

  const [rows, setRows] = useState<readonly VenuePhotoQueueItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPage = useCallback(
    async (status: VenuePhotoModerationStatus, page: number, silent = false) => {
      if (!silent) setLoading(true);
      setError(null);
      try {
        const offset = (page - 1) * PAGE_LIMIT;
        const data = await adminVenuePhotoApi.listQueue({
          status,
          limit: PAGE_LIMIT,
          offset,
        });
        setRows(data.rows);
        setTotal(data.total);
      } catch (err) {
        setError(translateApiError(err, t));
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [t],
  );

  // Re-fetch on tab change or page change.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchPage(statusParam, pageParam);
  }, [statusParam, pageParam, fetchPage]);

  // F61 (A1) — re-list silently (no skeleton flash) when the shared pending
  // count moves; the effect above does the first load.
  // `undefined` until the counts first load — that first arrival is not a change.
  const pendingCount = useNavCounts().counts.venuePhotos;
  const seenPendingCount = useRef(pendingCount);
  useEffect(() => {
    const previous = seenPendingCount.current;
    seenPendingCount.current = pendingCount;
    if (previous === undefined || pendingCount === undefined || previous === pendingCount) return;
    void fetchPage(statusParam, pageParam, /* silent */ true);
  }, [pendingCount, statusParam, pageParam, fetchPage]);

  const onStatusChange = (value: string) => {
    if (!isStatus(value)) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value !== DEFAULT_STATUS) next.set('status', value);
      else next.delete('status');
      next.delete('page'); // reset to page 1 on tab change
      return next;
    });
  };

  const onPageChange = (page: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (page > 1) next.set('page', String(page));
      else next.delete('page');
      return next;
    });
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_LIMIT));

  const columns = useMemo<readonly TableColumn<VenuePhotoQueueItem>[]>(
    () => [
      {
        key: 'thumbnail',
        header: t('admin:venue-editor.queue.headerThumbnail'),
        cell: (row) => {
          const src = row.photo.variants?.cardUrl ?? row.photo.cdnUrl;
          return (
            <div className="size-20 rounded-md overflow-hidden bg-surface-2 border border-hairline-2 shrink-0">
              {src ? (
                <img
                  src={src}
                  alt={row.photo.altText ?? ''}
                  className="size-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="size-full flex items-center justify-center text-ink-3">
                  <UIIcon name="eye" size={22} color="currentColor" />
                </div>
              )}
            </div>
          );
        },
      },
      {
        key: 'venue',
        header: t('admin:venue-editor.queue.headerVenue'),
        cell: (row) => (
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="font-medium text-[13px] text-ink truncate">{row.venueName}</span>
            <span className="font-mono text-[11.5px] text-ink-3 truncate">{row.venueSlug}</span>
          </div>
        ),
      },
      {
        key: 'actor',
        header: t('admin:venue-editor.queue.headerActor'),
        cell: (row) => (
          <span className="font-mono text-[11.5px] text-ink-3 truncate block">
            {row.ownerUserId ?? '—'}
          </span>
        ),
      },
      {
        key: 'uploaded',
        header: t('admin:venue-editor.queue.headerUploadedAt'),
        cell: (row) => (
          <span className="text-[12.5px] text-ink-2">
            {formatRelative(row.photo.createdAt, locale)}
          </span>
        ),
      },
      {
        key: 'status',
        header: t('admin:venue-editor.queue.headerStatus'),
        cell: (row) => (
          <Badge
            tone={
              row.photo.moderationStatus === 'approved'
                ? 'success'
                : row.photo.moderationStatus === 'rejected'
                  ? 'danger'
                  : 'warning'
            }
            size="sm"
          >
            {t(
              row.photo.moderationStatus === 'approved'
                ? 'admin:venue-editor.queue.filterApproved'
                : row.photo.moderationStatus === 'rejected'
                  ? 'admin:venue-editor.queue.filterRejected'
                  : 'admin:venue-editor.queue.filterPending',
            )}
          </Badge>
        ),
      },
      {
        key: 'actions',
        header: t('admin:venue-editor.queue.headerActions'),
        cell: (row) => (
          <Button
            variant="secondary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/venue-photos/${row.photo.id}`);
            }}
          >
            {t('admin:venue-editor.queue.openCta')}
          </Button>
        ),
      },
    ],
    [t, locale, navigate],
  );

  const renderBody = () => {
    if (loading) {
      return (
        <div className="space-y-3" data-testid="venue-photos-loading">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-20 w-full rounded-lg" />
          ))}
        </div>
      );
    }
    if (rows.length === 0) {
      return (
        <EmptyState
          icon={<UIIcon name="eye" size={36} color="currentColor" />}
          title={t('admin:venue-editor.queue.emptyTitle')}
          description={t('admin:venue-editor.queue.emptyBody')}
        />
      );
    }
    return (
      <>
        <Table
          data={rows as VenuePhotoQueueItem[]}
          columns={columns as TableColumn<VenuePhotoQueueItem>[]}
          rowKey={(row) => row.photo.id}
          onRowPress={(row) => navigate(`/venue-photos/${row.photo.id}`)}
          empty={t('admin:venue-editor.queue.emptyTitle')}
        />
        {pageCount > 1 ? (
          <div className="flex justify-center mt-4">
            <Pagination page={pageParam} pageCount={pageCount} onChange={onPageChange} />
          </div>
        ) : null}
      </>
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('admin:venue-editor.pageTitle.photoQueue')}
        subtitle={t('admin:venue-editor.queue.emptyBody')}
      />

      {error ? <Banner tone="danger">{error}</Banner> : null}

      <Tabs value={statusParam} defaultValue={DEFAULT_STATUS} onValueChange={onStatusChange}>
        <TabList aria-label={t('admin:venue-editor.pageTitle.photoQueue')}>
          {STATUS_TABS.map((tab) => (
            <Tab key={tab.value} value={tab.value}>
              {t(tab.labelKey)}
            </Tab>
          ))}
        </TabList>

        {STATUS_TABS.map((tab) => (
          <TabPanel key={tab.value} value={tab.value}>
            {renderBody()}
          </TabPanel>
        ))}
      </Tabs>
    </div>
  );
}
